import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { FileUploader } from 'react-drag-drop-files';
import DeleteLeadsForm from '@FormForms/DeleteLeadsForm';
import CreateLeadsForm from '@FormForms/CreateLeadsForm';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import Comment from '@CommonRepresentationalDisplay/Comment';
import { Autocomplete, Button, Drawer, IconButton, InputAdornment, MenuItem, Select, Skeleton, TextField, Tooltip } from '@mui/material';
import { X, Trash2, UserPlus, UserMinus, Edit3, PlusCircle, CheckCircle, XCircle, Eye, Sparkles, TrendingUp, Clock, PhoneCall,
    PauseCircle, Ban, Mail, Phone, MapPin, Building2, CalendarDays, Tag, MessageSquare, Paperclip, Send, Users, Activity, FileText, SquarePen } from 'lucide-react';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxFileStorage } from '@Hooks/reduxFileStorageHook/useReduxFileStorage';
import UserAvatar from '@CommonViews/UserAvatar';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import LeadsService from '@Helpers/URLHelpers/LeadsService';
import ErrorHandler from '@Root/ErrorHandler';
import './LeadsView.scss';

const LEAD_STATUSES = [
	{ key: 'newLeads', label: 'New Leads' },
	{ key: 'contacted', label: 'Contacted' },
	{ key: 'followUp', label: 'Follow Up' },
	{ key: 'active', label: 'Active' },
	{ key: 'onHold', label: 'On Hold' },
	{ key: 'onboarded', label: 'Onboarded' },
	{ key: 'closed', label: 'Closed' },
	{ key: 'lost', label: 'Lost' },
];

const DEFAULT_ASSIGNEE = {
	fullName: 'Unassigned',
	userName: 'unassigned',
	id: 0,
};

export default function LeadsView(props) {

	const [cursor, setCursor] = useState('auto');
	const [loading, setLoading] = useState(false);
	const [users, setUsers] = useState([DEFAULT_ASSIGNEE]);
	const [assigneeUser, setAssigneeUser] = useState(DEFAULT_ASSIGNEE);
	const [editAssignee, setEditAssignee] = useState(false);
	const [currentUserProjectPermissions, setCurrentUserProjectPermissions] = useState();
	const [showDeleteForm, setShowDeleteForm] = useState(false);
	const [showUpdateForm, setShowUpdateForm] = useState(false);
	const [selectedLead, setSelectedLead] = useState(null);
	const [createComment, setCreateComment] = useState({});
	const [createCommentText, setCreateCommentText] = useState('');
	const { currentProjectId, getUserPermissionByProjectId } = useReduxProject();
	const { fetchFileStorage, uploadFileInS3 } = useReduxFileStorage();
	const { activeUserId } = useReduxActiveUser();
	const projectService = new ProjectService();
	const leadsService = new LeadsService();
	const errorHandler = new ErrorHandler();

	useEffect(() => {
		if (props.open) {
			renderData();
		}
	}, [props.open, props.id]);

	const renderData = async () => {
		setLoading(true);
		if (props.id) {
			const lead = await fetchLead(props?.id);
			if (lead) {
				setSelectedLead(lead);
				await fetchFileStorage('LEAD', lead.id);
				setCurrentUserProjectPermissions(
					getUserPermissionByProjectId(lead.projectId),
				);
				await renderUsersAndTeamsListData(lead);
			}
		}
		setLoading(false);
	};

	const reRenderData = async (leadId) => {
        const lead = await fetchLead(leadId);
        if (lead) {
            setSelectedLead(lead);
            await fetchFileStorage('LEAD', leadId);
            setCurrentUserProjectPermissions(
                getUserPermissionByProjectId(lead.projectId),
            );
            await renderUsersAndTeamsListData(lead);
        }
    }

	const fetchLead = async (leadId) => {
		try {
			let response = await leadsService.getById(leadId);
			if (response.status < 400) {
				response = JSON.parse(response.body);
				return parseLeadsData(response);
			}
		} catch (err) {
			errorHandler.handleError(err);
		}
		return null;
	};

	const renderUsersAndTeamsListData = async (lead) => {
		try {
			const response =
				await projectService.getSimpleAssigneeUserListForProject(
					currentProjectId,
				);
			if (response.status < 400) {
				const userList = JSON.parse(response.body);
				const allUsers = [
					DEFAULT_ASSIGNEE,
					...userList.filter((user) => user.id !== DEFAULT_ASSIGNEE.id),
				];
				setUsers(allUsers);
				const assignedUser = allUsers.find(
					(user) => user.id === lead?.assigneeUserId,
				);
				setAssigneeUser(assignedUser || DEFAULT_ASSIGNEE);
			}
		} catch (err) {
			errorHandler.handleError(err);
		}
	};

	const safeJsonParse = (value, fallback = null) => {
		try {
			if (typeof value === 'string') {
				return JSON.parse(value);
			}
			return value;
		} catch {
			return fallback;
		}
	};

	const parseLeadsData = (item) => ({
		...item,
		details: safeJsonParse(item.details, {}),
		history: safeJsonParse(item.history, []),
		comments: safeJsonParse(item.comments, []),
	});

	const handleStatusChange = async (value) => {
		try {
			setCursor('wait');
			const bodyObj = {
				id: selectedLead?.id,
				status: value,
			};
			let response = await leadsService.updateStatus(bodyObj);
			if (response.status < 400) {
				response = JSON.parse(response.body);
				setSelectedLead(parseLeadsData(response));
				SnackbarManager.show('Lead status updated', 'success');
			} else {
				SnackbarManager.show(response.message, 'error');
			}
		} catch (err) {
			errorHandler.logError('Lead Status Change', err);
		} finally {
			setCursor('auto');
		}
	};

	const handleCommentChange = (e) => {
		const sanitizedComment = DOMPurify.sanitize(e.target.value);
		setCreateComment({
			text: sanitizedComment,
		});
		setCreateCommentText(sanitizedComment);
	};

	const handleSubmitComment = async () => {
		if (!createCommentText?.trim()) {
			return;
		}
		setCursor('wait');
		try {
			const comment = {
				...createComment,
				userId: activeUserId,
				timeStamp: Date.now(),
				pinnedComment: false,
			};
			const body = {
				id: selectedLead?.id,
				comment,
			};
			let response = await leadsService.updateComment(body);
			if (response.status < 400) {
				response = JSON.parse(response.body);
				setSelectedLead(parseLeadsData(response));
				setCreateComment({});
				setCreateCommentText('');
				SnackbarManager.show('Comment added', 'success');
			} else {
				SnackbarManager.show(response.message, 'error');
			}
		} catch (err) {
			errorHandler.logError('Comment Error', err);
			SnackbarManager.show('Unable to add comment', 'error');
		} finally {
			setCursor('auto');
		}
	};

	const handleSubmitCommentFile = async (file) => {
		if (!file) {
			return;
		}
		const fileSize = file.size / (1024 * 1024);
		if (fileSize > 1) {
			SnackbarManager.show('File size cannot exceed 1 MB', 'error');
			return;
		}
		setCursor('wait');
		try {
			const url = await convertFileToBase64(file);
			let newFileObj = {
				file: url,
				fileName: file.name,
				fileSize,
				fileType: file.type,
				projectId: currentProjectId,
			};
			const body = {
				fileName: file.name,
				tableType: 'LEAD',
				itemId: selectedLead?.id,
				file: newFileObj,
				projectId: currentProjectId,
				userId: activeUserId,
				storeType: 'comment',
			};
			newFileObj = await uploadFileInS3(body);
			const comment = {
				userId: activeUserId,
				type: 'File',
				fileId: newFileObj.id,
				timeStamp: Date.now(),
			};
			let response = await leadsService.updateComment({
				id: selectedLead?.id,
				comment,
			});
			if (response.status < 400) {
				response = JSON.parse(response.body);
				setSelectedLead(parseLeadsData(response));
				SnackbarManager.show('File attached', 'success');
			} else {
				SnackbarManager.show(response.message, 'error');
			}
		} catch (err) {
			errorHandler.logError('File Upload to Comments', err);
			SnackbarManager.show('Something went wrong', 'error');
		} finally {
			setCursor('auto');
		}
	};

	const convertFileToBase64 = (file) =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
		});

	const handleAssigneeChange = async (_event, value) => {
		if (!value) {
			return;
		}
		setCursor('wait');
		try {
			const body = {
				id: selectedLead?.id,
				assigneeUserId: value.id,
			};
			let response = await leadsService.updateAssignee(body);
			if (response.status < 400) {
				response = JSON.parse(response.body);
				setSelectedLead(parseLeadsData(response));
				setAssigneeUser(value);
				setEditAssignee(false);
				SnackbarManager.show('Assignee updated successfully', 'success');
			} else {
				SnackbarManager.show(response.message, 'error');
			}
		} catch (err) {
			errorHandler.logError('Assignee Change', err);
		} finally {
			setCursor('auto');
		}
	};

	const assignToSelf = () => {
		const user = users.find((item) => item.id === activeUserId);
		if (user) {
			handleAssigneeChange(null, user);
		}
	};

	const removeAssignee = () => {
		handleAssigneeChange(null, DEFAULT_ASSIGNEE);
	};

	const toggleDeleteForm = () => {
		setShowDeleteForm((prev) => !prev);
	};

	const toggleUpdateForm = () => {
	    console.log('calling')
		setShowUpdateForm((prev) => !prev);
	};

	const reloadDeleteData = () => {
		props?.reloadData();
		props?.onClose();
	};

	const getInitials = (string) => {
		if (!string) {
			return 'NA';
		}
		const parts = string.trim().split(/\s+/);
		if (parts.length > 1) {
			return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
		}
		return parts[0].substring(0, 2).toUpperCase();
	};

	const getStatusBadge = (status) => {
		const statusMap = {
			newLeads: {
				className: 'status-new',
				icon: <Sparkles size={13} />,
				label: 'New Lead',
			},
			contacted: {
				className: 'status-contacted',
				icon: <TrendingUp size={13} />,
				label: 'Contacted',
			},
			active: {
				className: 'status-active',
				icon: <Clock size={13} />,
				label: 'Active',
			},
			followUp: {
				className: 'status-followup',
				icon: <PhoneCall size={13} />,
				label: 'Follow Up',
			},
			onHold: {
				className: 'status-hold',
				icon: <PauseCircle size={13} />,
				label: 'On Hold',
			},
			onboarded: {
				className: 'status-onboarded',
				icon: <CheckCircle size={13} />,
				label: 'Onboarded',
			},
			closed: {
				className: 'status-closed',
				icon: <XCircle size={13} />,
				label: 'Closed',
			},
			lost: {
				className: 'status-lost',
				icon: <Ban size={13} />,
				label: 'Lost',
			},
		};
		return (
			statusMap[status] || {
				className: 'status-default',
				icon: null,
				label: status,
			}
		);
	};

	const formatDate = (timestamp) => {
		if (!timestamp) {
			return '-';
		}
		const date = new Date(timestamp);
		const now = new Date();
		const diffMs = now - date;
		const diffDays = Math.floor(diffMs / 86400000);
		if (diffDays === 0) {
			return 'Today';
		}
		if (diffDays === 1) {
			return 'Yesterday';
		}
		if (diffDays < 7) {
			return `${diffDays} days ago`;
		}
		return date.toLocaleDateString(undefined, {
			day: 'numeric',
			month: 'short',
			year: 'numeric',
		});
	};

	const formatExactDate = (timestamp) => {
		if (!timestamp) {
			return '-';
		}
		return new Date(timestamp).toLocaleDateString(undefined, {
			day: 'numeric',
			month: 'short',
			year: 'numeric',
		});
	};

	const getActivityIcon = (property) => {
		switch (property) {
			case 'Created':
				return <PlusCircle size={15} />;
			case 'assigneeAdded':
				return <UserPlus size={15} />;
			case 'assigneeRemoved':
				return <UserMinus size={15} />;
			case 'Status':
				return <Edit3 size={15} />;
			case 'completed':
				return <CheckCircle size={15} />;
			case 'closed':
				return <XCircle size={15} />;
			default:
				return <Eye size={15} />;
		}
	};

	const status = getStatusBadge(selectedLead?.status);

	return (
		<Drawer
			anchor='right'
			open={props?.open}
			onClose={props?.onClose}
			PaperProps={{
				className: 'lead-drawer',
			}}
		>
			<div className='lead-details' style={{ cursor }}>
				{loading ? (
					<LeadDetailsLoader />
				) : (
					<>
						<header className='lead-details__header'>
							<div className='lead-details__identity'>
								<div className='lead-details__avatar'>
									{getInitials(selectedLead?.name)}
								</div>
								<div className='lead-details__identity-info'>
									<div className='lead-details__eyebrow'>Lead Details</div>
									<h1>{selectedLead?.name || 'Unnamed Lead'}</h1>
									{selectedLead?.companyName && (
										<div className='lead-details__company'>
											<Building2 size={14} />
											{selectedLead.companyName}
										</div>
									)}
								</div>
							</div>
							<div className='lead-details__header-actions'>
								<Select
									value={selectedLead?.status || 'newLeads'}
									onChange={(e) => handleStatusChange(e.target.value)}
									variant='standard'
									disableUnderline
									IconComponent={()=> null}
                                    sx={{
                                        '& .MuiSelect-select': {
                                            padding: '0 !important',
                                            display: 'flex',
                                            alignItems: 'center'
                                        },
                                        '& svg': {
                                            color: 'inherit !important',
                                        },
                                    }}
									renderValue={(value) => {
										const item = getStatusBadge(value);
										return (
											<span className={`lead-status ${item.className}`}>
												{item.icon}
												{item.label}
											</span>
										);
									}}
									className='lead-status-select'
								>
									{LEAD_STATUSES.map((item) => {
										const itemStatus = getStatusBadge(item.key);
										return (
											<MenuItem key={item.key} value={item.key}>
												<span className={`lead-status ${itemStatus.className}`}>
													{itemStatus.icon}
													{itemStatus.label}
												</span>
											</MenuItem>
										);
									})}
								</Select>
								<Tooltip title='Edit lead'>
									<IconButton
										onClick={toggleUpdateForm}
										className='header-icon-btn header-icon-btn'
									>
										<SquarePen size={17} />
									</IconButton>
								</Tooltip>
								<Tooltip title='Delete lead'>
									<IconButton
										onClick={toggleDeleteForm}
										className='header-icon-btn header-icon-btn--danger'
									>
										<Trash2 size={17} />
									</IconButton>
								</Tooltip>
								<Tooltip title='Close'>
									<IconButton
										onClick={props?.onClose}
										className='header-icon-btn'
									>
										<X size={18} />
									</IconButton>
								</Tooltip>
							</div>
						</header>
						<main className='lead-details__content'>
							{/* Notes */}
							{selectedLead?.query && (
								<section className='lead-note'>
									<div className='lead-note__icon'>
										<FileText size={17} />
									</div>
									<div>
										<span>Lead Notes</span>
										<p>{selectedLead.query}</p>
									</div>
								</section>
							)}
							{/* Contact */}
							<section className='lead-section'>
								<SectionHeader
									icon={<UserPlus size={16} />}
									title='Contact Information'
								/>
								<div className='contact-grid'>
									<ContactCard
										icon={<Mail size={17} />}
										label='Email'
										value={selectedLead?.email}
										href={
											selectedLead?.email
												? `mailto:${selectedLead.email}`
												: undefined
										}
									/>
									<ContactCard
										icon={<Phone size={17} />}
										label='Phone'
										value={selectedLead?.contactNumber}
										href={
											selectedLead?.contactNumber
												? `tel:${selectedLead.contactNumber}`
												: undefined
										}
									/>
									<ContactCard
										icon={<MapPin size={17} />}
										label='Location'
										value={selectedLead?.address}
									/>
									<ContactCard
										icon={<Building2 size={17} />}
										label='Company'
										value={selectedLead?.companyName}
									/>
								</div>
							</section>
							{/* Assignee */}
							<section className='lead-section'>
								<SectionHeader
									icon={<Users size={16} />}
									title='Assignee'
									action={
										<div className='assignee-actions'>
											{assigneeUser?.id !== Number(activeUserId) && (
												<Button
													size='small'
													onClick={assignToSelf}
													className='assign-btn assign-btn--secondary'
												>
													Assign to me
												</Button>
											)}
											<Button
												size='small'
												onClick={() => setEditAssignee((prev) => !prev)}
												className='assign-btn'
											>
												{editAssignee ? 'Cancel' : 'Change'}
											</Button>
										</div>
									}
								/>
								<div className='assignee-card'>
									{editAssignee ? (
										<Autocomplete
											options={users}
											value={assigneeUser}
											onChange={handleAssigneeChange}
											getOptionLabel={(option) =>
												`${option.fullName} (@${option.userName})`
											}
											isOptionEqualToValue={(option, value) =>
												option.id === value.id
											}
											fullWidth
											size='small'
											renderInput={(params) => (
												<TextField
													{...params}
													placeholder='Search team member...'
													autoFocus
												/>
											)}
											renderOption={(optionProps, option) => (
												<li {...optionProps}>
													<UserAvatar profileId={option.id} size='small' />
													<div className='assignee-option'>
														<strong>{option.fullName}</strong>
														<span>@{option.userName}</span>
													</div>
												</li>
											)}
										/>
									) : (
										<>
											<div className='assignee-card__person'>
												<UserAvatar
													profileId={assigneeUser?.id}
													size='medium'
													popover
												/>
												<div>
													<strong>{assigneeUser?.fullName}</strong>
													<span>@{assigneeUser?.userName}</span>
												</div>
											</div>
											{assigneeUser?.id > 0 &&
												currentUserProjectPermissions?.allowCreate && (
													<Tooltip title='Remove assignee'>
														<IconButton
															size='small'
															onClick={removeAssignee}
															className='remove-assignee'
														>
															<X size={15} />
														</IconButton>
													</Tooltip>
												)}
										</>
									)}
								</div>
							</section>
							{/* Lead Metadata */}
							<section className='lead-section'>
								<SectionHeader
									icon={<Tag size={16} />}
									title='Lead Information'
								/>
								<div className='metadata-grid'>
									<MetadataCard
										icon={<Tag size={15} />}
										label='Source'
										value={selectedLead?.source || 'Not specified'}
									/>
									<MetadataCard
										icon={<CalendarDays size={15} />}
										label='Created'
										value={formatExactDate(selectedLead?.createTimeStamp)}
									/>
									<MetadataCard
										icon={<Activity size={15} />}
										label='Current status'
										value={status.label}
									/>
									<MetadataCard
										icon={<MessageSquare size={15} />}
										label='Comments'
										value={`${selectedLead?.comments?.length || 0} comments`}
									/>
								</div>
							</section>
							{/* Activity / Comments */}
							<div className='lead-activity-layout'>
								{/* Activity */}
								<section className='lead-section activity-section'>
									<SectionHeader
										icon={<Activity size={16} />}
										title='Activity'
									/>
									<div className='activity-timeline'>
										{selectedLead?.history?.length ? (
											selectedLead.history.map((activity) => (
												<div key={activity.id} className='activity-item'>
													<div
														className={`activity-item__icon activity-item__icon--${activity.property}`}
													>
														{getActivityIcon(activity.property)}
													</div>
													<div className='activity-item__body'>
														<div className='activity-item__top'>
															<strong>{activity?.changeByObj?.fullName}</strong>
															<span>{formatDate(activity.timeStamp)}</span>
														</div>
														<p>{activity.description}</p>
													</div>
												</div>
											))
										) : (
											<EmptyState
												icon={<Activity size={18} />}
												text='No activity yet'
											/>
										)}
									</div>
								</section>
								{/* Comments */}
								<section className='lead-section comments-section'>
									<SectionHeader
										icon={<MessageSquare size={16} />}
										title='Comments'
										count={selectedLead?.comments?.length}
									/>
									<div className='comments-list'>
										{selectedLead?.comments?.length ? (
											selectedLead.comments.map((comment, index) => (
												<Comment comment={comment} key={index} />
											))
										) : (
											<EmptyState
												icon={<MessageSquare size={18} />}
												text='No comments yet'
											/>
										)}
									</div>
									<div className='comment-composer'>
										<TextField
											fullWidth
											multiline
											maxRows={4}
											value={createCommentText}
											onChange={handleCommentChange}
											placeholder='Write a comment...'
											size='small'
											className='comment-composer__input'
											onKeyDown={(e) => {
												if (e.key === 'Enter' && !e.shiftKey) {
													e.preventDefault();
													handleSubmitComment();
												}
											}}
											InputProps={{
												endAdornment: (
													<InputAdornment position='end'>
														<div className='comment-composer__actions'>
															<FileUploader
																handleChange={handleSubmitCommentFile}
																name='leadCommentFile'
																types={[
																	'JPG',
																	'JPEG',
																	'PNG',
																	'PDF',
																	'DOC',
																	'DOCX',
																]}
															>
																<Tooltip title='Attach file'>
																	<IconButton size='small' component='span'>
																		<Paperclip size={17} />
																	</IconButton>
																</Tooltip>
															</FileUploader>
															<Tooltip title='Send comment'>
																<IconButton
																	size='small'
																	onClick={handleSubmitComment}
																	disabled={!createCommentText?.trim()}
																	className='comment-send'
																>
																	<Send size={16} />
																</IconButton>
															</Tooltip>
														</div>
													</InputAdornment>
												),
											}}
										/>
										<div className='comment-composer__hint'>
											Press Enter to send · Shift + Enter for a new line
										</div>
									</div>
								</section>
							</div>
						</main>
					</>
				)}
				{showDeleteForm && (
					<DeleteLeadsForm
						open={showDeleteForm}
						closePopup={toggleDeleteForm}
						lead={selectedLead}
						reloadData={reloadDeleteData}
					/>
				)}

				{showUpdateForm && (
					<CreateLeadsForm
						open={showUpdateForm}
						onClose={toggleUpdateForm}
						contact={selectedLead}
						type='update'
						reloadData={reRenderData}
					/>
				)}
			</div>
		</Drawer>
	);
}

function SectionHeader({ icon, title, action, count }) {
	return (
		<div className='section-header'>
			<div className='section-header__title'>
				<div className='section-header__icon'>{icon}</div>
				<h2>{title}</h2>
				{count > 0 && <span className='section-header__count'>{count}</span>}
			</div>
			{action}
		</div>
	);
}

function ContactCard({ icon, label, value, href }) {
	if (!value) {
		return null;
	}
	return (
		<div className='contact-card'>
			<div className='contact-card__icon'>{icon}</div>
			<div className='contact-card__content'>
				<span>{label}</span>
				{href ? <a href={href}>{value}</a> : <strong>{value}</strong>}
			</div>
		</div>
	);
}

function MetadataCard({ icon, label, value }) {
	return (
		<div className='metadata-card'>
			<div className='metadata-card__icon'>{icon}</div>
			<div>
				<span>{label}</span>
				<strong>{value}</strong>
			</div>
		</div>
	);
}

function EmptyState({ icon, text }) {
	return (
		<div className='empty-state'>
			<div className='empty-state__icon'>{icon}</div>
			<span>{text}</span>
		</div>
	);
}

function LeadDetailsLoader() {
	return (
		<div className='lead-loader'>
			<div className='lead-loader__header'>
				<Skeleton variant='circular' width={48} height={48} />
				<div>
					<Skeleton variant='text' width={190} height={28} />
					<Skeleton variant='text' width={130} />
				</div>
			</div>
			<Skeleton variant='rounded' height={90} />
			<Skeleton variant='text' width={180} height={28} />
			<div className='lead-loader__grid'>
				<Skeleton variant='rounded' height={70} />
				<Skeleton variant='rounded' height={70} />
				<Skeleton variant='rounded' height={70} />
				<Skeleton variant='rounded' height={70} />
			</div>
			<Skeleton variant='text' width={140} height={28} />
			{[1, 2, 3].map((item) => (
				<div className='lead-loader__activity' key={item}>
					<Skeleton variant='circular' width={34} height={34} />
					<div>
						<Skeleton variant='text' width={150} />
						<Skeleton variant='text' width={260} />
					</div>
				</div>
			))}
		</div>
	);
}
